// src/components/equipements/ExportMontage.tsx — SCE Issues
// Bouton « Exporter » de la fiche équipement (27/09) : un seul bouton, deux
// choix — la feuille « Montage et Tests » ELEC / AUTOM ou MECA.
//
// Les choix s'ouvrent SOUS le bouton, dans le flux de la page, plutôt qu'en
// menu flottant : au téléphone, rien ne déborde ni ne se cache sous le doigt.
// Chaque choix annonce son nombre de problèmes ; à zéro, il est grisé.

import { useMemo, useState } from 'react'
import { ChevronDown, Download, Loader2 } from 'lucide-react'
import { Bulle, GroupeBulles } from '@/components/ui/Bulle'
import { Button } from '@/components/ui/Button'
import { useRapportMontage } from '@/hooks/useRapportMontage'
import { BADGES_CLOTURE, type BadgeCloture, type Equipement, type Issue } from '@/types/issue.types'
import type { ReferenceEDE } from '@/utils/documentationEDE'
import {
  GABARITS_RAPPORT,
  LIBELLES_GABARIT,
  filtrerPourRapport,
  problemesDuRapport,
} from '@/utils/rapportMontage'

interface ExportMontageProps {
  equipement: Equipement
  /** Les problèmes de CET équipement ; le tri par métier et statut est fait ici. */
  issues: readonly Issue[]
  /** Schéma électrique le plus récent de l'équipement (en-tête ELEC). */
  refEDE: ReferenceEDE | undefined
  /** Vrai tant que les problèmes se chargent : export impossible. */
  chargement: boolean
}

export function ExportMontage({ equipement, issues, refEDE, chargement }: ExportMontageProps): JSX.Element {
  const [ouvert, setOuvert] = useState(false)
  const [seulementNonClos, setSeulementNonClos] = useState(false)
  const [badges, setBadges] = useState<ReadonlySet<BadgeCloture>>(new Set())
  const { exporter, enCours } = useRapportMontage()

  // Le filtre s'applique avant tout : les nombres annoncés sont ceux du fichier.
  const retenus = useMemo(
    () => filtrerPourRapport(issues, { seulementNonClos, badges }),
    [issues, seulementNonClos, badges],
  )

  function basculerBadge(badge: BadgeCloture): void {
    setBadges((actuels) => {
      const suivants = new Set(actuels)
      if (!suivants.delete(badge)) suivants.add(badge)
      return suivants
    })
  }

  return (
    <div className="flex flex-col gap-2">
      <Button
        variant="secondary"
        fullWidth
        onClick={() => setOuvert((o) => !o)}
        disabled={chargement}
        aria-expanded={ouvert}
        iconLeft={<Download size={18} />}
        iconRight={
          <ChevronDown size={18} style={{ transform: ouvert ? 'rotate(180deg)' : undefined, transition: 'transform 0.15s' }} />
        }
      >
        Exporter (Excel)
      </Button>

      {ouvert && (
        <div className="flex flex-col gap-2">
          <GroupeBulles libelle="Problèmes à exporter" multiple>
            <Bulle
              role="checkbox"
              libelle="Non clos seulement"
              selectionne={seulementNonClos}
              onClick={() => setSeulementNonClos((v) => !v)}
            />
          </GroupeBulles>
          <span style={{ fontSize: 'var(--font-size-info)', color: 'var(--color-text-secondary)' }}>
            Badge qualité (l'un des) — aucun coché : tous les problèmes
          </span>
          <GroupeBulles libelle="Badge qualité" multiple>
            {BADGES_CLOTURE.map((badge) => (
              <Bulle
                key={badge}
                role="checkbox"
                libelle={badge}
                selectionne={badges.has(badge)}
                onClick={() => basculerBadge(badge)}
              />
            ))}
          </GroupeBulles>
        </div>
      )}

      {ouvert && (
        <div className="grid grid-cols-2 gap-2">
          {GABARITS_RAPPORT.map((gabarit) => {
            const n = problemesDuRapport(retenus, gabarit).length
            const actif = enCours === gabarit
            return (
              <button
                key={gabarit}
                type="button"
                disabled={n === 0 || enCours !== null}
                onClick={() => void exporter(gabarit, equipement, retenus, refEDE)}
                className="flex flex-col items-start justify-center text-left disabled:opacity-40"
                style={{
                  minHeight: 56,
                  padding: '8px 12px',
                  borderRadius: 8,
                  border: '1px solid var(--color-gray-20)',
                  backgroundColor: 'var(--color-white)',
                  color: 'var(--color-black)',
                  cursor: n === 0 ? 'default' : 'pointer',
                }}
              >
                <span className="flex items-center gap-2" style={{ fontSize: 'var(--font-size-copy2)', fontWeight: 600 }}>
                  {actif && <Loader2 size={14} className="animate-spin" />}
                  {LIBELLES_GABARIT[gabarit]}
                </span>
                <span style={{ fontSize: 'var(--font-size-info)', color: 'var(--color-text-secondary)' }}>
                  {n === 0 ? 'Aucun problème' : `${n} problème${n > 1 ? 's' : ''}`}
                </span>
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}
